/*
 * Bundle Builder
 * Reads the device taxonomy (device_family / device_generation metaobjects) and per-product
 * variant tables emitted by sections/bundle-builder.liquid, and drives the whole configurator:
 * family -> generation cascade, variant resolution, color swatches, summary, add to cart.
 *
 * <product-bundle-builder> (the main product section's "Bundle builder" block, rendered by
 * snippets/product-bundle-builder.liquid) reuses all of it on product pages, taking the device
 * from the variant picked in the product form instead.
 */
(() => {
  if (customElements.get('bundle-builder')) return;

  // Product page guided flow: the sticky bar prices a preview cart through the Storefront API to show
  // the store's real automatic discount (carts need no access token)
  const STOREFRONT_API_VERSION = '2026-07';
  const BUNDLE_PREVIEW_QUERY = `mutation BundlePreview($lines: [CartLineInput!]!, $buyer: CartBuyerIdentityInput) {
    cartCreate(input: { lines: $lines, buyerIdentity: $buyer }) {
      cart {
        discountAllocations { discountedAmount { amount } }
        lines(first: 100) {
          nodes {
            attributes { key value }
            cost { subtotalAmount { amount } }
            discountAllocations { discountedAmount { amount } }
          }
        }
      }
    }
  }`;

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-bb-config]').textContent);
      this.config.families.sort((a, b) => a.position - b.position);

      this.products = {};
      this.readProducts(this);

      // Section-level entries are fallbacks; the theme's own swatch config
      // (Theme settings -> Color swatches, same source as product pages) wins.
      this.colorMap = this.parseColorMap(this.config.colorMap || '');
      Object.assign(this.colorMap, this.parseColorMap(this.config.themeSwatches || ''));
      this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.dataset.currency || 'USD' });

      this.state = { family: null, gen: null };
      this.cards = this.collectCards();

      this.els = {
        families: this.querySelector('[data-bb-families]'),
        genStep: this.querySelector('[data-bb-gen-step]'),
        generations: this.querySelector('[data-bb-generations]'),
        chips: this.querySelector('[data-bb-chips]'),
        familyLabel: this.querySelector('[data-bb-family-label]'),
        genLabel: this.querySelector('[data-bb-gen-label]'),
        frame: this.querySelector('[data-bb-frame]'),
        image: this.querySelector('[data-bb-image]'),
        defaultVisual: this.querySelector('[data-bb-default-visual]'),
        lines: this.querySelector('[data-bb-lines]'),
        empty: this.querySelector('[data-bb-empty]'),
        total: this.querySelector('[data-bb-total]'),
        add: this.querySelector('[data-bb-add]'),
        summary: this.querySelector('.bb-summary'),
        sticky: this.querySelector('[data-bb-sticky]'),
        stickyTotal: this.querySelector('[data-bb-sticky-total]'),
        stickyAdd: this.querySelector('[data-bb-sticky-add]')
      };
      this.defaultTitle = this.els.familyLabel?.textContent;

      this.storageKey = `bundle-builder:${window.location.pathname}`;

      this.renderFamilies();
      this.bindCards(this.cards);
      this.els.add.addEventListener('click', () => this.addToCart());

      // Sticky total bar: shows while the bundle has items and the full summary
      // card is off screen, so the total and CTA are always within reach.
      this.summaryInView = false;
      this.selectedCount = 0;
      if (this.els.sticky) {
        this.els.stickyAdd.addEventListener('click', () => this.addToCart());
        new IntersectionObserver((entries) => {
          this.summaryInView = entries[0].isIntersecting;
          this.updateSticky();
        }, { threshold: 0.1 }).observe(this.els.summary);
      }

      this.init();
    }

    async init() {
      // A device page ships only its own family. If the customer arrives wanting another
      // one (family URL param or saved state), fetch the full builder before restoring.
      const wanted = new URLSearchParams(window.location.search).get(this.dataset.deviceParamFamily) || this.savedState()?.family;
      if (wanted && wanted !== this.config.presetFamily) await this.loadAllFamilies();

      this.restoreState();
      this.applyUrlParams();
      this.refresh();
    }

    readProducts(root) {
      root.querySelectorAll('[data-bb-product]').forEach((el) => {
        const data = JSON.parse(el.textContent);
        this.products[data.id] = data;
      });
    }

    collectCards() {
      return Array.from(this.querySelectorAll('[data-bb-card]')).map((el) => ({
        el,
        product: this.products[el.dataset.productId],
        splitName: el.dataset.splitName || null,
        splitValue: el.dataset.splitValue || null,
        // Card with its own add button (product page block): swatches always show, and
        // picking a color only changes the color; the button does the adding
        addButton: el.classList.contains('bb-option--add-button'),
        group: el.closest('[data-bb-group]'),
        selected: false,
        color: null,
        matched: [],
        current: null
      })).filter((c) => c.product);
    }

    /* ---------- other families, loaded on demand (device pages) ---------- */

    loadAllFamilies() {
      if (!this.config.partial) return Promise.resolve();
      if (!this.loadingAll) {
        const url = `${this.config.allModelsUrl}?section_id=${encodeURIComponent(this.config.sectionId)}`;
        this.setAttribute('aria-busy', 'true');
        this.loadingAll = fetch(url)
          .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.text(); })
          .then((html) => this.mergeAllFamilies(new DOMParser().parseFromString(html, 'text/html')))
          .catch((e) => {
            // Leave the page usable with its own family; a later switch retries
            console.error('bundle-builder: could not load other device families', e);
            this.loadingAll = null;
          })
          .finally(() => this.removeAttribute('aria-busy'));
      }
      return this.loadingAll;
    }

    mergeAllFamilies(doc) {
      const source = doc.querySelector('bundle-builder');
      if (!source) throw new Error('builder not found in fetched section');

      this.readProducts(source);

      source.querySelectorAll('x-modal[data-bb-product-modal]').forEach((modal) => {
        if (!document.getElementById(modal.id)) this.appendChild(document.adoptNode(modal));
      });

      // Swap in the full card set per group, carrying over what the customer already picked
      const previous = new Map(this.cards.map((c) => [this.cardKey(c), c]));
      this.querySelectorAll('[data-bb-group]').forEach((group) => {
        const from = source.querySelector(`[data-bb-group][data-group-id="${CSS.escape(group.dataset.groupId)}"] .bb-options`);
        if (from) group.querySelector('.bb-options').replaceChildren(...Array.from(from.children).map((n) => document.adoptNode(n)));
      });
      this.cards = this.collectCards();
      this.cards.forEach((card) => {
        const old = previous.get(this.cardKey(card));
        if (old) { card.selected = old.selected; card.color = old.color; }
      });
      this.bindCards(this.cards);
      this.config.partial = false;
    }

    /* ---------- selection persistence (survives navigation to product pages) ---------- */

    cardKey(card) {
      return `${card.group ? card.group.dataset.groupId : ''}:${card.product.id}:${card.splitValue || ''}`;
    }

    saveState() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          family: this.state.family,
          gen: this.state.gen,
          cards: this.cards
            .filter((c) => c.selected || c.color)
            .map((c) => ({ key: this.cardKey(c), selected: c.selected, color: c.color }))
        }));
      } catch (e) { /* storage unavailable (private mode etc.) — persistence is best-effort */ }
    }

    savedState() {
      try { return JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch (e) { return null; }
    }

    restoreState() {
      const data = this.savedState();
      if (!data) return;

      if (data.family && this.config.families.some((f) => f.key === data.family)) {
        this.state.family = data.family;
        this.renderGenerations();
        const fam = this.familyDef();
        if (typeof data.gen === 'string' && (data.gen === this.state.gen || fam.generations.some((g) => g.key === data.gen))) {
          this.state.gen = data.gen;
        }
      }

      (data.cards || []).forEach((saved) => {
        const card = this.cards.find((c) => this.cardKey(c) === saved.key);
        if (card) {
          card.selected = Boolean(saved.selected);
          card.color = saved.color || null;
        }
      });
    }

    parseColorMap(text) {
      // Accepts both "name | value" (section fallbacks) and the theme's
      // "Name:value" swatch config; value may be a hex or a CSS gradient.
      const map = {};
      text.split('\n').forEach((line) => {
        const sep = line.includes('|') ? line.indexOf('|') : line.indexOf(':');
        if (sep < 1) return;
        const name = line.slice(0, sep).trim().toLowerCase();
        const value = line.slice(sep + 1).trim();
        if (name && value) map[name] = value;
      });
      return map;
    }

    swatchColor(name) {
      return this.colorMap[name.toLowerCase()] || name.toLowerCase();
    }

    /* ---------- family / generation cascade ---------- */

    renderFamilies() {
      if (!this.els.families) return; // preset-family page: no family step rendered
      this.els.families.innerHTML = '';
      this.config.families.forEach((fam) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bb-option bb-option--standalone';
        btn.innerHTML = `<span class="bb-option__name">${fam.label}</span>`;
        btn.setAttribute('aria-pressed', 'false');
        btn.addEventListener('click', () => this.setFamily(fam.key));
        btn.dataset.key = fam.key;
        this.els.families.appendChild(btn);
      });
    }

    async setFamily(key) {
      if (this.state.family === key) return;
      this.pendingFamily = key;
      if (key !== this.config.presetFamily) await this.loadAllFamilies();
      // Customer picked something else (or the same family twice) while loading
      if (this.pendingFamily !== key || this.state.family === key) return;
      this.state.family = key;
      this.state.gen = null;
      this.renderGenerations();
      this.refresh();
    }

    familyDef() {
      return this.config.families.find((f) => f.key === this.state.family) || null;
    }

    genHasProducts(famKey, genKey) {
      return Object.values(this.products).some((p) => p.variants.some((v) =>
        v.available && v.families.includes(famKey) &&
        (v.generations.length === 0 || v.generations.includes(genKey))
      ));
    }

    renderGenerations() {
      const fam = this.familyDef();
      this.els.generations.innerHTML = '';
      if (!fam) { this.els.genStep.hidden = true; return; }

      if (fam.generations.length === 0) {
        // Family without linked generations (e.g. MacBook Neo): no generation step.
        // Only wildcard variants (empty generation metafield) match such a family.
        this.state.gen = '';
        this.els.genStep.hidden = true;
        return;
      }

      // Only cross-check against variant data once at least one variant is actually tagged;
      // during setup (no products assigned or no metafields populated) show the full list.
      const anyTagged = Object.values(this.products).some((p) => p.variants.some((v) => v.families.length > 0));
      if (!anyTagged) {
        console.warn('bundle-builder: no configured product variant carries device metafields yet (custom.mf_device_family) — showing all generations unfiltered.');
      }

      // Every generation defined on the family renders; ones without any matching
      // tagged product are disabled rather than hidden.
      const enabled = fam.generations.filter((g) => !anyTagged || this.genHasProducts(fam.key, g.key));
      fam.generations.forEach((gen) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bb-option bb-option--standalone';
        btn.innerHTML = `<span class="bb-option__name">${gen.label}</span>`;
        btn.setAttribute('aria-pressed', 'false');
        btn.dataset.key = gen.key;
        btn.disabled = !enabled.includes(gen);
        btn.addEventListener('click', () => { this.state.gen = gen.key; this.refresh(); });
        this.els.generations.appendChild(btn);
      });

      // A single available generation is auto-selected but stays visible so the
      // customer can see what was chosen for them.
      if (enabled.length === 1) this.state.gen = enabled[0].key;
      this.els.genStep.hidden = false;
    }

    /* ---------- card resolution ---------- */

    bindCards(cards) {
      cards.forEach((card) => {
        card.el.querySelector('[data-bb-card-toggle]').addEventListener('click', () => this.toggleCard(card));
      });
    }

    toggleCard(card, forceOn) {
      const mode = card.group.dataset.groupMode;
      const turnOn = forceOn || !card.selected;
      if (turnOn && mode === 'single') {
        this.cards.forEach((c) => { if (c !== card && c.group === card.group) c.selected = false; });
      }
      card.selected = turnOn;
      this.refresh();
    }

    matchVariants(card, { family, gen } = this.state) {
      if (!family || gen === null) return [];
      const splitIdx = card.splitName ? card.product.options.indexOf(card.splitName) : -1;
      // gen === '' means the selected family has no generation axis (e.g. MacBook Neo):
      // any variant tagged with the family matches, whatever its generation list says.
      return card.product.variants.filter((v) =>
        v.available &&
        v.families.includes(family) &&
        (gen === '' || v.generations.length === 0 || v.generations.includes(gen)) &&
        (splitIdx === -1 || v.options[splitIdx] === card.splitValue)
      );
    }

    colorIndex(product) {
      return product.options.findIndex((o) => /colou?r/i.test(o));
    }

    refresh() {
      const ready = Boolean(this.state.family) && this.state.gen !== null;

      this.syncPressed(this.els.families, this.state.family);
      this.syncPressed(this.els.generations, this.state.gen);

      this.cards.forEach((card) => {
        card.matched = this.matchVariants(card);
        const visible = ready && card.matched.length > 0;
        card.el.hidden = !visible;
        if (!visible) { card.selected = false; card.current = null; return; }

        const cIdx = this.colorIndex(card.product);
        const colors = cIdx === -1 ? [] : [...new Set(card.matched.map((v) => v.options[cIdx]))];
        if (card.color === null || !colors.includes(card.color)) card.color = colors[0] || null;
        card.current = cIdx === -1 || !card.color
          ? card.matched[0]
          : card.matched.find((v) => v.options[cIdx] === card.color) || card.matched[0];

        card.el.setAttribute('aria-pressed', String(card.selected));
        const addButton = card.el.querySelector('[data-bb-add-button]');
        if (addButton) {
          addButton.setAttribute('aria-pressed', String(card.selected));
          addButton.textContent = card.selected ? addButton.dataset.addedText : addButton.dataset.addText;
        }
        // Thumbnail follows the selected variant (e.g. its color) when that variant has its own image
        const thumb = card.el.querySelector('[data-bb-card-image]');
        const thumbSrc = card.current.image || card.product.image;
        if (thumb && thumbSrc && thumb.getAttribute('src') !== thumbSrc) thumb.src = thumbSrc;
        this.renderPrice(card.el.querySelector('[data-bb-card-price]'), card.current);
        this.renderSwatches(card, colors);

        // "added" indicator: lives on the swatch row when that row is visible,
        // otherwise as the standalone element (bottom text on desktop, checkmark
        // under the price on mobile — CSS decides which of the two renders).
        const swatchesHidden = card.el.querySelector('[data-bb-swatches]').hidden;
        card.el.querySelectorAll('[data-bb-added-solo]').forEach((el) => {
          el.hidden = !card.selected || !swatchesHidden;
        });
      });

      this.querySelectorAll('[data-bb-group]').forEach((group) => {
        group.hidden = !ready;
        const empty = group.querySelector('[data-bb-group-empty]');
        if (empty) empty.hidden = this.cards.some((c) => c.group === group && !c.el.hidden);
      });

      this.updateSummary();
      this.updateUrl();
      this.saveState();
    }

    syncPressed(container, key) {
      if (!container) return;
      container.querySelectorAll('button[data-key]').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.key === key));
      });
    }

    renderPrice(el, variant) {
      // Same classes the theme's price-list uses on product pages
      el.innerHTML = '';
      if (variant.compare > variant.price) {
        const compare = document.createElement('s');
        compare.className = 'text-subdued line-through';
        compare.textContent = this.money.format(variant.compare / 100);
        const price = document.createElement('span');
        price.className = 'text-on-sale';
        price.textContent = this.money.format(variant.price / 100);
        el.append(compare, ' ', price);
      } else {
        el.textContent = this.money.format(variant.price / 100);
      }
    }

    renderSwatches(card, colors) {
      const wrap = card.el.querySelector('[data-bb-swatches]');
      const list = wrap.querySelector('.bb-swatches__list');
      if ((!card.selected && !card.addButton) || colors.length < 2) { wrap.hidden = true; return; }
      wrap.hidden = false;
      list.innerHTML = '';
      colors.forEach((color) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bb-swatch';
        btn.style.background = this.swatchColor(color);
        btn.setAttribute('aria-label', color);
        btn.setAttribute('aria-pressed', String(color === card.color));
        btn.addEventListener('click', () => {
          card.color = color;
          if (card.addButton) this.refresh();
          else this.toggleCard(card, true);
        });
        list.appendChild(btn);
      });
      wrap.querySelector('[data-bb-swatch-value]').textContent = card.color || '';
    }

    /* ---------- summary / url / cart ---------- */

    selectedCards() {
      return this.cards.filter((c) => c.selected && c.current && !c.el.hidden);
    }

    updateVisual(fam, gen) {
      if (!this.els.familyLabel) return; // product add-ons have no device visual

      this.els.familyLabel.textContent = fam ? this.dataset.familyTitle.replace('[family]', fam.label) : this.defaultTitle;
      this.els.genLabel.innerHTML = gen ? gen.label : '&nbsp;';
      this.els.genLabel.classList.toggle('is-empty', !gen);

      const imageSrc = (gen && gen.image) || (fam && fam.image) || null;
      if (imageSrc) {
        if (this.els.image.getAttribute('src') !== imageSrc) this.els.image.src = imageSrc;
        this.els.image.alt = fam ? `${fam.label} bundle` : '';
        this.els.image.hidden = false;
        this.els.defaultVisual.hidden = true;
      } else {
        this.els.image.hidden = true;
        this.els.defaultVisual.hidden = false;
      }
    }

    updateSummary() {
      const fam = this.familyDef();
      const gen = fam ? fam.generations.find((g) => g.key === this.state.gen) : null;
      const selected = this.selectedCards();

      this.updateVisual(fam, gen);

      this.els.lines.innerHTML = '';
      let total = 0;
      selected.forEach((card) => {
        total += card.current.price;
        const parts = [card.product.title];
        if (card.splitValue) parts.push(card.splitValue);
        if (card.color) parts.push(card.color);
        const line = document.createElement('div');
        line.className = 'bb-summary__line';
        line.innerHTML = `<span></span><span></span>`;
        line.children[0].textContent = parts.join(' — ');
        line.children[1].textContent = this.money.format(card.current.price / 100);
        this.els.lines.appendChild(line);
      });

      this.els.empty.hidden = selected.length > 0;
      this.els.total.textContent = this.money.format(total / 100);
      this.els.add.disabled = selected.length === 0;

      this.selectedCount = selected.length;
      if (this.els.stickyTotal) this.els.stickyTotal.textContent = this.money.format(total / 100);
      this.updateSticky();

      if (this.els.chips) {
        this.els.chips.innerHTML = '';
        const chips = [];
        if (fam) chips.push(fam.label + (gen ? ` · ${gen.label}` : ''));
        selected.forEach((card) => chips.push(card.product.title));
        chips.forEach((text) => {
          const chip = document.createElement('div');
          chip.className = 'bb-chip';
          chip.textContent = text;
          this.els.chips.appendChild(chip);
        });
      }
    }

    updateSticky() {
      if (!this.els.sticky) return;
      this.els.sticky.hidden = this.selectedCount === 0 || this.summaryInView;
    }

    applyUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const famKey = params.get(this.dataset.deviceParamFamily) || this.config.presetFamily;
      const genKey = params.get(this.dataset.deviceParamGen);
      const fam = this.config.families.find((f) => f.key === famKey);
      if (!fam) return;
      if (fam.key !== this.state.family) {
        this.state.family = fam.key;
        this.renderGenerations();
      }
      if (genKey && fam.generations.some((g) => g.key === genKey) && this.genHasProducts(fam.key, genKey)) {
        this.state.gen = genKey;
      }
    }

    updateUrl() {
      const params = new URLSearchParams(window.location.search);
      const set = (name, value) => value ? params.set(name, value) : params.delete(name);
      // On a preset-family landing page the family is implied by the URL itself
      set(this.dataset.deviceParamFamily, this.state.family === this.config.presetFamily ? null : this.state.family);
      set(this.dataset.deviceParamGen, this.state.gen);
      const query = params.toString();
      history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
    }

    async addToCart(buttons = [this.els.add, this.els.stickyAdd].filter(Boolean)) {
      const selected = this.selectedCards();
      if (selected.length === 0) return false;

      const bundleKey = this.state.gen ? `${this.state.family}-${this.state.gen}` : this.state.family;
      const items = selected.map((card) => ({
        id: card.current.id,
        quantity: 1,
        properties: { _bundle: bundleKey }
      }));

      const labels = buttons.map((b) => b.textContent);
      let added = false;
      buttons.forEach((b) => { b.disabled = true; b.textContent = '…'; });

      try {
        // Ask cart-aware sections (cart drawer, cart count...) which section ids
        // they need re-rendered, so the add response carries their fresh HTML.
        const sectionsToBundle = new Set();
        document.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { detail: { sections: sectionsToBundle } }));

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            sections: [...sectionsToBundle].join(','),
            sections_url: window.location.pathname
          })
        });
        const responseJson = await response.json();

        if (response.ok) {
          added = true;
          if (window.themeVariables?.settings.cartType === 'page') {
            window.location.href = `${window.Shopify.routes.root}cart`;
            return added;
          }

          // Mirror the theme's product form: full cart payload + rendered sections,
          // then cart:change with baseEvent "variant:add" (this opens the drawer).
          const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
          cartContent.sections = responseJson.sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent }
          }));
        } else {
          document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: responseJson.message, tone: 'error' } }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } catch (e) {
        document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: 'Could not add bundle to cart.', tone: 'error' } }));
      } finally {
        const idle = this.selectedCards().length === 0;
        buttons.forEach((b, i) => { b.textContent = labels[i]; b.disabled = idle; });
      }
      return added;
    }
  }

  customElements.define('bundle-builder', BundleBuilder);

  /*
   * Product page add-ons: the bundle builder's cards without the family / generation steps.
   * The device comes from the variant picked in the product form (its mf_device_family /
   * mf_device_generation metafields). A variant that fits several devices gets a small
   * chooser limited to those devices; nothing is preselected so no wrong add-on is offered.
   */
  class ProductBundleBuilder extends BundleBuilder {
    init() {
      this.variantDevices = JSON.parse(this.querySelector('[data-bb-variant-devices]').textContent);
      this.els.content = this.querySelector('[data-bb-content]');
      this.els.deviceStep = this.querySelector('[data-bb-device-step]');
      this.els.devices = this.querySelector('[data-bb-devices]');
      this.els.editorNote = this.querySelector('[data-bb-editor-note]');
      this.devices = [];

      // The theme's product form announces each variant change, bubbling up to document.
      // Only this product's own variants count (not quick-add forms elsewhere on the page).
      this.onVariantChange = (event) => {
        const id = event.detail?.variant?.id;
        if (id && this.variantDevices[id]) this.setVariant(id);
      };
      document.addEventListener('variant:change', this.onVariantChange);

      if (this.config.guided) this.setupGuided();
      this.setVariant(this.config.initialVariant);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange);
      if (this.onFormSubmit) document.removeEventListener('submit', this.onFormSubmit, true);
      if (this.onCartChange) document.removeEventListener('cart:change', this.onCartChange);
      this.els.bar?.remove();
    }

    deviceKey({ family, gen }) {
      return `${family}|${gen}`;
    }

    // Every (family, generation) pair a variant fits. A variant tagged with none of a family's
    // generations fits all of them; a family without generations (e.g. MacBook Neo) uses gen ''.
    devicesFor(variantId) {
      const tags = this.variantDevices[variantId];
      if (!tags) return [];
      return this.config.families.filter((fam) => tags.families.includes(fam.key)).flatMap((fam) => {
        if (fam.generations.length === 0) return [{ family: fam.key, gen: '', label: fam.label, sub: '' }];
        const tagged = fam.generations.filter((g) => tags.generations.includes(g.key));
        return (tagged.length ? tagged : fam.generations).map((g) => ({ family: fam.key, gen: g.key, label: fam.label, sub: g.label }));
      });
    }

    setVariant(variantId) {
      // Devices without any compatible add-on are left out
      this.devices = this.devicesFor(variantId).filter((d) => this.cards.some((card) => this.matchVariants(card, d).length > 0));

      // Keep the customer's device when the new variant still fits it (e.g. only the color changed)
      const currentKey = this.state.family ? this.deviceKey(this.state) : null;
      const next = this.devices.find((d) => this.deviceKey(d) === currentKey) || (this.devices.length === 1 ? this.devices[0] : null);
      this.state.family = next ? next.family : null;
      this.state.gen = next ? next.gen : null;

      this.renderDevices();
      this.refresh();
    }

    renderDevices() {
      this.els.devices.innerHTML = '';
      this.els.deviceStep.hidden = this.devices.length < 2;
      if (this.devices.length < 2) return;

      this.devices.forEach((device) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bb-option bb-option--standalone';
        btn.dataset.key = this.deviceKey(device);
        btn.setAttribute('aria-pressed', 'false');
        btn.innerHTML = '<span><span class="bb-option__name"></span><span class="bb-option__sub" style="display: block;"></span></span>';
        btn.querySelector('.bb-option__name').textContent = device.label;
        if (device.sub) btn.querySelector('.bb-option__sub').textContent = device.sub;
        else btn.querySelector('.bb-option__sub').remove();
        btn.addEventListener('click', () => {
          this.state.family = device.family;
          this.state.gen = device.gen;
          this.refresh();
        });
        this.els.devices.appendChild(btn);
      });
    }

    refresh() {
      super.refresh();
      const ready = Boolean(this.state.family) && this.state.gen !== null;
      this.syncPressed(this.els.devices, ready ? this.deviceKey(this.state) : null);

      // Only groups with a compatible card for this device show
      this.querySelectorAll('[data-bb-group]').forEach((group) => {
        group.hidden = !ready || !this.cards.some((c) => c.group === group && !c.el.hidden);
      });
      // The guided flow's sticky bar replaces the in-block summary
      this.els.summary.hidden = !ready || this.config.guided;

      // Nothing to offer for this variant (no device tags, or no compatible add-on): the block
      // disappears, except in the theme editor where a note explains why. Its product-info wrapper
      // is hidden with it, so no empty gap is left between the neighbouring blocks.
      const nothing = this.devices.length === 0;
      const designMode = Boolean(window.Shopify?.designMode);
      this.els.content.hidden = nothing;
      if (this.els.editorNote) this.els.editorNote.hidden = !(nothing && designMode);
      (this.closest('.product-info__block-item') || this).hidden = nothing && !designMode;

      if (this.els.bar) this.updateBar();
    }

    /* ---------- guided flow: "Next" button + sticky bundle bar ---------- */

    setupGuided() {
      const bar = this.querySelector('[data-bb-bar]');
      Object.assign(this.els, {
        bar,
        barList: bar.querySelector('[data-bb-bar-list]'),
        barToggle: bar.querySelector('[data-bb-bar-toggle]'),
        barCount: bar.querySelector('[data-bb-bar-count]'),
        barTotal: bar.querySelector('[data-bb-bar-total]'),
        barSavings: bar.querySelector('[data-bb-bar-savings]'),
        barAdd: bar.querySelector('[data-bb-bar-add]')
      });
      // Fixed to the screen: lives in <body>, out of the (sticky) product info column
      document.body.appendChild(bar);
      this.els.barToggle.addEventListener('click', () => this.setBarExpanded(this.els.barToggle.getAttribute('aria-expanded') !== 'true'));
      this.els.barAdd.addEventListener('click', () => this.finish());

      // The cart line added with "Next", remembered for the session so the bar survives a reload
      this.mainLine = null;
      this.mainLineKey = `bundle-builder:main:${this.config.productId}`;

      // "Next": take over the product form's submit before the theme's own handler (which would
      // open the cart drawer). Only while this block has add-ons for the selected variant;
      // otherwise the button keeps working as a plain add to cart.
      this.onFormSubmit = (event) => {
        // getAttribute: the form's own "id" input shadows form.id
        if (event.target.getAttribute('id') !== this.config.productFormId || this.devices.length === 0) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.next(event.target);
      };
      document.addEventListener('submit', this.onFormSubmit, true);

      // Keep the bar in step when the product is removed or its quantity changed in the cart
      this.onCartChange = (event) => {
        const cart = event.detail?.cart;
        if (!cart) return;
        this.cartItems = cart.items;
        const line = this.mainLine && cart.items.find((item) => item.key === this.mainLine.key);
        if (this.mainLine && (!line || line.quantity !== this.mainLine.quantity)) this.setMainLine(line || null);
        else if (!this.els.bar.hidden) this.updateBar(); // other cart lines count toward discount tiers
      };
      document.addEventListener('cart:change', this.onCartChange);

      this.restoreMainLine();
    }

    bundledSections() {
      // Cart-aware sections (drawer, count...) ask to be re-rendered with the cart response
      const sections = new Set();
      document.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { detail: { sections } }));
      return [...sections].join(',');
    }

    async next(form) {
      if (!form.checkValidity()) { form.reportValidity(); return; }
      const formData = new FormData(form);
      const variantId = Number(formData.get('id'));

      // Already added with Next and still in the cart: just move on to the add-ons
      if (this.mainLine && this.mainLine.variantId === variantId) { this.scrollToBuilder(); return; }

      const buttons = Array.from(form.elements).filter((el) => el.type === 'submit');
      if (buttons.some((b) => b.getAttribute('aria-busy') === 'true')) return;
      buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      const root = window.Shopify.routes.root;

      try {
        // A different variant than the one added earlier replaces it
        if (this.mainLine) {
          await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: this.mainLine.key, quantity: 0 })
          });
        }

        formData.set('sections', this.bundledSections());
        formData.set('sections_url', window.location.pathname);
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' } // lets Shopify check inventory
        });
        const responseJson = await response.json();
        if (!response.ok) {
          document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: responseJson.description || responseJson.message, tone: 'error' } }));
          return;
        }

        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = responseJson.sections;
        this.cartItems = cart.items;
        const added = responseJson.items ? responseJson.items[0] : responseJson;
        this.setMainLine(cart.items.find((item) => item.key === added.key) || null);

        // Refreshes the cart count and drawer; not a "variant:add" event, so the drawer stays closed
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'bundle-builder:next', cart }
        }));
        this.scrollToBuilder();
      } catch (e) {
        document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: 'Could not add to cart.', tone: 'error' } }));
      } finally {
        buttons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    }

    scrollToBuilder() {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setMainLine(line) {
      this.mainLine = line
        ? { key: line.key, variantId: line.variant_id, quantity: line.quantity, linePrice: line.original_line_price }
        : null;
      try {
        if (this.mainLine) sessionStorage.setItem(this.mainLineKey, JSON.stringify({ key: this.mainLine.key }));
        else sessionStorage.removeItem(this.mainLineKey);
      } catch (e) { /* storage unavailable: the bar just won't survive a reload */ }
      this.refresh();
    }

    async restoreMainLine() {
      let saved = null;
      try { saved = JSON.parse(sessionStorage.getItem(this.mainLineKey) || 'null'); } catch (e) { /* no storage */ }
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      this.cartItems = cart.items;
      if (saved) this.setMainLine(cart.items.find((item) => item.key === saved.key) || null);
    }

    barItems() {
      // The product added with Next, then the selected add-ons
      const items = [];
      if (this.mainLine) {
        items.push({ variantId: this.mainLine.variantId, title: this.config.productTitle, quantity: this.mainLine.quantity, price: this.mainLine.linePrice });
      }
      this.selectedCards().forEach((card) => {
        items.push({ variantId: card.current.id, title: card.product.title, quantity: 1, price: card.current.price });
      });
      return items;
    }

    updateBar() {
      const items = this.barItems();
      this.els.bar.hidden = items.length === 0;
      if (items.length === 0) { this.setBarExpanded(false); return; }

      // Titles only: variant details are in the cart
      this.els.barCount.textContent = items.reduce((n, item) => n + item.quantity, 0);
      this.els.barList.replaceChildren(...items.map((item) => {
        const row = document.createElement('div');
        row.className = 'bb-bar__item';
        row.innerHTML = '<span></span><span></span>';
        row.children[0].textContent = item.quantity > 1 ? `${item.title} × ${item.quantity}` : item.title;
        row.children[1].textContent = this.money.format(item.price / 100);
        return row;
      }));
      this.els.barAdd.disabled = false;
      this.requestSavings(items);
    }

    renderBarTotals(items, savings) {
      const format = (cents) => this.money.format(cents / 100);
      const total = items.reduce((n, item) => n + item.price, 0);
      this.els.barTotal.replaceChildren();
      if (savings > 0) {
        const before = document.createElement('s');
        before.textContent = format(total);
        this.els.barTotal.append(before);
      }
      this.els.barTotal.append(format(total - savings));
      this.els.barSavings.textContent = savings > 0 ? `${this.config.savingsLabel} ${format(savings)}` : '';
    }

    // The savings come from Shopify itself, so they always follow the store's automatic discounts
    // (tiers like 5% on 2 / 10% on 3 included) with nothing to keep in sync in the theme. While a
    // new amount is on its way, the previous totals stay up, dimmed.
    requestSavings(items) {
      const others = (this.cartItems || []).filter((line) => line.key !== this.mainLine?.key);
      const signature = JSON.stringify([
        items.map((item) => [item.variantId, item.quantity]),
        others.map((line) => [line.variant_id, line.quantity])
      ]);
      this.savingsCache ??= new Map();
      if (this.savingsCache.has(signature)) {
        this.savingsSignature = signature;
        clearTimeout(this.savingsTimer);
        this.els.bar.classList.remove('is-pricing');
        this.renderBarTotals(items, this.savingsCache.get(signature));
        return;
      }
      if (signature === this.savingsSignature) return; // already on its way

      this.savingsSignature = signature;
      if (!this.els.barTotal.textContent) this.renderBarTotals(items, 0);
      this.els.bar.classList.add('is-pricing');
      clearTimeout(this.savingsTimer);
      this.savingsTimer = setTimeout(async () => {
        let savings = 0;
        try {
          savings = await this.previewDiscount(items, others);
          this.savingsCache.set(signature, savings);
        } catch (e) {
          console.warn('bundle-builder: could not get the bundle discount from Shopify', e);
        }
        if (signature !== this.savingsSignature) return; // the bundle changed meanwhile
        this.els.bar.classList.remove('is-pricing');
        this.renderBarTotals(items, savings);
      }, 300);
    }

    // Prices a throwaway cart through the Storefront API (no token needed for carts): the customer's
    // other cart lines plus the bundle, so tier minimums count everything they'll check out with.
    // Returns the discount on the bundle's own lines, in cents.
    async previewDiscount(items, others) {
      const marker = { key: '_bundle_preview', value: '1' };
      const variant = (id) => `gid://shopify/ProductVariant/${id}`;
      const lines = [
        ...others.map((line) => ({ merchandiseId: variant(line.variant_id), quantity: line.quantity })),
        // The marker keeps bundle lines apart from identical ones already in the cart
        ...items.map((item) => ({ merchandiseId: variant(item.variantId), quantity: item.quantity, attributes: [marker] }))
      ];
      const country = window.Shopify?.country;
      const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: BUNDLE_PREVIEW_QUERY,
          variables: { lines, buyer: country ? { countryCode: country } : null }
        })
      });
      const cart = (await response.json()).data?.cartCreate?.cart;
      if (!cart) throw new Error(`preview cart not created (HTTP ${response.status})`);

      const cents = (money) => Math.round(parseFloat(money.amount) * 100);
      const discountOf = (allocations) => allocations.reduce((n, a) => n + cents(a.discountedAmount), 0);
      let bundleSubtotal = 0;
      let allSubtotal = 0;
      let bundleDiscount = 0;
      cart.lines.nodes.forEach((line) => {
        const subtotal = cents(line.cost.subtotalAmount);
        allSubtotal += subtotal;
        if (line.attributes.some((a) => a.key === marker.key)) {
          bundleSubtotal += subtotal;
          bundleDiscount += discountOf(line.discountAllocations);
        }
      });
      // Order-level discounts aren't tied to lines: the bundle gets its share by value
      const orderDiscount = discountOf(cart.discountAllocations);
      if (orderDiscount && allSubtotal) bundleDiscount += Math.round(orderDiscount * bundleSubtotal / allSubtotal);
      return bundleDiscount;
    }

    setBarExpanded(expanded) {
      this.els.barToggle.setAttribute('aria-expanded', String(expanded));
      this.els.barList.hidden = !expanded;
    }

    // Sticky bar button: add the selected add-ons (the theme then opens the cart drawer)
    async finish() {
      if (this.selectedCards().length > 0) {
        if (!(await this.addToCart([this.els.barAdd]))) return;
        this.cards.forEach((card) => { card.selected = false; });
      } else {
        this.openCart();
      }
      // Everything is in the cart now: the bar starts over
      this.setBarExpanded(false);
      this.setMainLine(null);
    }

    openCart() {
      const drawer = document.querySelector('cart-drawer');
      if (window.themeVariables?.settings.cartType === 'drawer' && drawer?.show) drawer.show();
      else window.location.href = `${window.Shopify.routes.root}cart`;
    }

    // The product page owns its URL and the customer's selections don't outlive the page
    updateUrl() {}
    saveState() {}
  }

  customElements.define('product-bundle-builder', ProductBundleBuilder);
})();
